import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

/**
 * Componente wrapper para proteger rutas
 * Redirige a /login si el usuario no esta autenticado
 * Opcionalmente restringe por roles
 */
export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // Mostrar loading mientras se verifica la sesion
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-slate-400 animate-spin" />
          <p className="text-slate-500">Verificando sesion...</p>
        </div>
      </div>
    );
  }

  // Redirigir a login si no esta autenticado
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar roles si se especifican
  if (allowedRoles && allowedRoles.length > 0 && user) {
    const userRole = user.role.name.toUpperCase();
    const normalizedRoles = allowedRoles.map(r => r.toUpperCase());

    if (!normalizedRoles.includes(userRole)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-slate-800 mb-2">Acceso Denegado</h2>
            <p className="text-slate-500 mb-6">
              No tienes permisos para acceder a esta seccion. 
              Contacta con el administrador si crees que es un error.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Volver
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}

/**
 * HOC para crear rutas protegidas con roles
 */
export function withRoles(...roles: string[]) {
  return function ProtectedWithRoles({ children }: { children: React.ReactNode }) {
    return <ProtectedRoute allowedRoles={roles}>{children}</ProtectedRoute>;
  };
}

// Componentes preconfigurados para roles comunes
export const AdminRoute = withRoles('ADMIN');
export const ManagerRoute = withRoles('ADMIN', 'GERENTE');
export const OfficeRoute = withRoles('ADMIN', 'GERENTE', 'OFICINA');
export const ShopFloorRoute = withRoles('ADMIN', 'TALLER', 'JEFE_TALLER');
